import { Labeled } from '@components/form/Labeled'
import { useCreateMeter } from '@services/meters/mutations/useCreateMeter'
import { useCreateMeterReading } from '@services/meters/mutations/useCreateMeterReading'
import { useDeleteMeter } from '@services/meters/mutations/useDeleteMeter'
import { useDeleteMeterReading } from '@services/meters/mutations/useDeleteMeterReading'
import { useReplaceMeter } from '@services/meters/mutations/useReplaceMeter'
import { useMeters } from '@services/meters/queries/useMeters'
import type { Meter, MeterType } from '@services/meters/types'
import { Gauge, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionCard } from '@/features/settlements/components/SectionCard'
import { formatDate } from '@/features/settlements/components/format'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

const METER_TYPES: MeterType[] = [
  'HOT_WATER',
  'COLD_WATER',
  'ELECTRICITY',
  'HEAT'
]

function AddMeterDialog({ flatId }: { flatId: string }) {
  const { t } = useTranslation('settlement')
  const create = useCreateMeter()

  const [open, setOpen] = useState(false)
  const [type, setType] = useState<MeterType>('HOT_WATER')
  const [serialNumber, setSerialNumber] = useState('')
  const [location, setLocation] = useState('')
  const [installedAt, setInstalledAt] = useState<string | undefined>()

  const onSubmit = () =>
    create.mutate(
      {
        flat: flatId,
        type,
        serial_number: serialNumber,
        location,
        installed_at: installedAt ?? null
      },
      {
        onSuccess: () => {
          setOpen(false)
          setSerialNumber('')
          setLocation('')
          setInstalledAt(undefined)
        }
      }
    )

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Plus className="mr-1.5 h-4 w-4" />
        {t('meters.add')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('meters.add')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Labeled label={t('meters.type')}>
              <Combobox
                value={type}
                searchable={false}
                options={METER_TYPES.map((value) => ({
                  value,
                  label: t(`meter_types.${value}`)
                }))}
                onChange={(value) =>
                  setType((value as MeterType) ?? 'HOT_WATER')
                }
              />
            </Labeled>
            <Labeled label={t('meters.serial_number')}>
              <Input
                value={serialNumber}
                onChange={(event) => setSerialNumber(event.target.value)}
              />
            </Labeled>
            <Labeled label={t('meters.location')}>
              <Input
                value={location}
                onChange={(event) => setLocation(event.target.value)}
              />
            </Labeled>
            <Labeled label={t('meters.installed_at')}>
              <DatePicker
                value={installedAt}
                clearable
                onChange={setInstalledAt}
              />
            </Labeled>

            <div className="flex justify-end">
              <Button onClick={onSubmit} disabled={create.isPending}>
                {t('actions.add')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function ReplaceMeterDialog({ meter }: { meter: Meter }) {
  const { t } = useTranslation('settlement')
  const replace = useReplaceMeter()

  const [open, setOpen] = useState(false)
  const [removedAt, setRemovedAt] = useState<string | undefined>()
  const [serialNumber, setSerialNumber] = useState('')

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
        <RefreshCw className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('meters.replace')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="m-0 text-sm text-muted-foreground">
              {t('meters.replace_hint')}
            </p>
            <Labeled label={t('meters.removed_at')}>
              <DatePicker value={removedAt} onChange={setRemovedAt} />
            </Labeled>
            <Labeled label={t('meters.serial_number')}>
              <Input
                value={serialNumber}
                onChange={(event) => setSerialNumber(event.target.value)}
              />
            </Labeled>

            <div className="flex justify-end">
              <Button
                disabled={!removedAt || replace.isPending}
                onClick={() =>
                  removedAt &&
                  replace.mutate(
                    {
                      id: meter.id,
                      body: {
                        removed_at: removedAt,
                        serial_number: serialNumber
                      }
                    },
                    { onSuccess: () => setOpen(false) }
                  )
                }
              >
                {t('meters.replace')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function MeterRow({ meter }: { meter: Meter }) {
  const { t } = useTranslation('settlement')
  const { confirm, dialog } = useConfirm()

  const addReading = useCreateMeterReading()
  const deleteReading = useDeleteMeterReading()
  const deleteMeter = useDeleteMeter()

  const [date, setDate] = useState<string | undefined>()
  const [value, setValue] = useState('')

  return (
    <div className="rounded-2xl border border-ink/[0.08] p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-semibold text-ink">
          {t(`meter_types.${meter.type}`)}
        </span>
        {meter.serial_number && (
          <span className="text-sm text-muted-foreground">
            {meter.serial_number}
          </span>
        )}
        {meter.location && (
          <span className="text-sm text-muted-foreground">
            · {meter.location}
          </span>
        )}
        {!meter.is_active && (
          <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] text-muted-foreground">
            {t('meters.inactive')} {formatDate(meter.removed_at)}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {meter.is_active && <ReplaceMeterDialog meter={meter} />}
          <Button
            variant="ghost"
            size="icon"
            onClick={async () => {
              if (
                await confirm({
                  title: t('meters.delete_confirm'),
                  destructive: true
                })
              ) {
                deleteMeter.mutate(meter.id)
              }
            }}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {meter.readings.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          {t('meters.no_readings')}
        </p>
      ) : (
        <div className="space-y-1">
          {meter.readings.map((reading) => (
            <div
              key={reading.id}
              className="flex items-center gap-3 text-sm text-ink"
            >
              <span className="w-24 text-muted-foreground">
                {formatDate(reading.date)}
              </span>
              <span className="tabular-nums font-medium">{reading.value}</span>
              <span className="text-xs text-muted-foreground">
                {t(`reading_sources.${reading.source}`)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto"
                onClick={async () => {
                  if (
                    await confirm({
                      title: t('meters.delete_reading_confirm'),
                      destructive: true
                    })
                  ) {
                    deleteReading.mutate(reading.id)
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <div className="w-40">
          <DatePicker
            value={date}
            clearable
            placeholder={t('meters.reading_date')}
            onChange={setDate}
          />
        </div>
        <Input
          className="w-32"
          inputMode="decimal"
          placeholder={t('meters.reading_value')}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <Button
          variant="outline"
          disabled={!date || !value || addReading.isPending}
          onClick={() =>
            date &&
            addReading.mutate(
              { meterId: meter.id, body: { date, value } },
              {
                onSuccess: () => {
                  setValue('')
                  setDate(undefined)
                }
              }
            )
          }
        >
          {t('meters.add_reading')}
        </Button>
      </div>

      {dialog}
    </div>
  )
}

export function FlatMeters({ flatId }: { flatId: string }) {
  const { t } = useTranslation('settlement')
  const { data, isLoading } = useMeters({ flat: flatId })

  return (
    <SectionCard
      title={t('meters.title')}
      icon={Gauge}
      action={<AddMeterDialog flatId={flatId} />}
    >
      {isLoading ? (
        <div className="h-24 animate-pulse rounded-2xl bg-ink/[0.04]" />
      ) : !data || data.results.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">{t('meters.empty')}</p>
      ) : (
        <div className="space-y-3">
          {data.results.map((meter) => (
            <MeterRow key={meter.id} meter={meter} />
          ))}
        </div>
      )}
    </SectionCard>
  )
}
